import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { track } from '../analytics.js';
import { ApiError, api } from '../api/client.js';
import { useSession } from '../api/session.js';

const NICKNAME_RE = /^[\p{L}\p{N}_-]+$/u;
/** Odczekanie po ostatnim naciśnięciu klawisza, zanim odpytamy serwer o dostępność nicku. */
const NICKNAME_CHECK_DEBOUNCE_MS = 400;

type NicknameStatus = 'idle' | 'checking' | 'available' | 'taken';

/**
 * Samodzielna rejestracja przez link zaproszenia (`docs/07-roadmapa.md`, Faza 3).
 * `kod`/`ref` w adresie (patrz link generowany w `LobbyPage.tsx`) tylko wypełniają
 * pola domyślnie — dalej edytowalne, gdyby ktoś wpisywał kod ręcznie.
 */
export function RegisterPage() {
  const { user, loading, register } = useSession();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const ref = search.get('ref');

  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [inviteCode, setInviteCode] = useState(search.get('kod') ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [nicknameStatus, setNicknameStatus] = useState<NicknameStatus>('idle');
  const latestNicknameRef = useRef(nickname);
  latestNicknameRef.current = nickname;

  // Podpowiedź dostępności nicku "na bieżąco" — walidacja formatu jest od razu (lokalnie,
  // bez sieci), sprawdzenie zajętości u serwera dopiero po odczekaniu, żeby nie odpytywać
  // przy każdym naciśnięciu klawisza. Ostateczne rozstrzygnięcie i tak robi `submit`
  // (baza jest jedynym prawdziwym źródłem prawdy) — to tylko wcześniejsza podpowiedź.
  useEffect(() => {
    const trimmed = nickname.trim();
    if (trimmed.length < 3 || trimmed.length > 20 || !NICKNAME_RE.test(trimmed)) {
      setNicknameStatus('idle');
      return;
    }
    setNicknameStatus('checking');
    const timer = window.setTimeout(() => {
      api
        .nicknameAvailable(trimmed)
        .then((result) => {
          // Nick w polu mógł się zmienić, zanim odpowiedź wróciła — nie nadpisujmy
          // świeższego stanu spóźnioną odpowiedzią na stare zapytanie.
          if (latestNicknameRef.current.trim() !== trimmed) return;
          setNicknameStatus(result.available ? 'available' : 'taken');
        })
        .catch(() => {
          if (latestNicknameRef.current.trim() === trimmed) setNicknameStatus('idle');
        });
    }, NICKNAME_CHECK_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [nickname]);

  if (loading) return <p className="app__status">Sprawdzanie sesji…</p>;
  if (user) return <Navigate to="/" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await register({
        nickname,
        password,
        email: email.trim() ? email.trim() : undefined,
        inviteCode,
        ref: ref ?? undefined,
      });
      navigate('/', { replace: true });
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'Nie udało się założyć konta');
      const code = problem instanceof ApiError ? problem.code : 'UNKNOWN';
      track('error_shown', { props: { code } });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card card--narrow" onSubmit={submit}>
      <h2 className="hand">Załóż konto</h2>
      <label className="field">
        <span>Nick</span>
        <input
          value={nickname}
          onChange={(event) => setNickname(event.target.value)}
          autoComplete="username"
          minLength={3}
          maxLength={20}
          required
        />
        <span
          className={`field__hint${
            nicknameStatus === 'available' ? ' field__hint--ok' : ''
          }${nicknameStatus === 'taken' ? ' field__hint--bad' : ''}`}
          aria-live="polite"
        >
          {nicknameStatus === 'checking' && 'Sprawdzanie dostępności…'}
          {nicknameStatus === 'available' && '✓ Nick jest dostępny'}
          {nicknameStatus === 'taken' && '✗ Ten nick jest już zajęty'}
        </span>
      </label>
      <label className="field">
        <span>Hasło</span>
        <input
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          minLength={8}
          required
        />
      </label>
      <label className="field">
        <span>E-mail (opcjonalnie)</span>
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
        />
      </label>
      <label className="field">
        <span>Kod zaproszenia</span>
        <input
          value={inviteCode}
          onChange={(event) => setInviteCode(event.target.value)}
          required
        />
      </label>
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}
      <button type="submit" className="btn btn--primary" disabled={busy}>
        {busy ? 'Zakładanie konta…' : 'Załóż konto'}
      </button>
      <p className="app__note">
        Masz już konto? <Link to="/login">Zaloguj się</Link>.
      </p>
    </form>
  );
}
